import { useTranslation } from 'react-i18next';

import { Box } from '@material-ui/core';

import type { ShowDialogType } from '@admin/shared/context/dialog';
import { UnsavedChangesIcon } from '@admin/shared/components/icons/custom/unsaved-changes';
import { DialogSizes, useDialogStyles } from '@admin/shared/components/dialogs/dialog';

/**
 * Type of component props
 */
export type ShowConfirmChangesDialogType = (
  onSave: () => Promise<void>,
  onDiscard: () => void,
) => void;

/**
 * Open dialog to prompt user how to handle
 * unsaved changes.
 *
 * Options:
 *  - save changes
 *  - discard changes
 *  - cancel, go back to current view
 *
 * @param showDialog - dialog handler
 *
 * @returns {ShowConfirmChangesDialogType}
 */
export const useConfirmChangesDialog = (
  showDialog: ShowDialogType,
): ShowConfirmChangesDialogType => {
  const { t } = useTranslation();
  const styles = useDialogStyles();

  return (onSave, onDiscard) => {
    showDialog({
      size: DialogSizes.LARGE,
      children: (
        <Box display="flex" flexDirection="column" alignItems="center">
          <UnsavedChangesIcon className={styles.icon} />
          <p className={styles.headline}>{t('common:dialogs.confirmChanges.title')}</p>
          <p className={styles.text}>{t('common:dialogs.confirmChanges.text')}</p>
        </Box>
      ),
      actions: [
        {
          label: t('common:actions.save'),
          color: 'primary',
          action: onSave,
        },
        {
          label: t('common:actions.cancel'),
          autoFocus: true,
        },
        {
          label: t('common:actions.noSave'),
          action: onDiscard,
        },
      ],
    });
  };
};
